import {useAddNotification, useTransition} from '@yandex-delivery/blocks'
import {useFormContext} from '@yandex-delivery/form'
import {identity, log, logGroupEnd, logGroupStart, merge, pick, pipe, tap} from '@yandex-delivery/utils'
import {memo} from 'react'
import {useLocation} from 'react-router-dom'

import {
  fetchAddressFromInitialProps,
  fetchInitialLocation,
  fetchPreOrderForm,
  fetchRouteFromQuery,
  getSourceCity,
  withEmptyDestinationPoint,
} from 'src/shared/api'
import {appConfig} from 'src/shared/config'
import {type Order as FormValues} from 'src/shared/types'

import {restoreFormValues} from '../../consts'
import {withTariff} from './utils/with-tariff'
import {withZonePoint} from './utils/with-zone-point'

type Props = {
  defaultValues: FormValues
}

/**
 * @description как оказалось, [асинхронный дефолтный стейт](https://react-hook-form.com/docs/useform#defaultValues)
 * плохо работает с хуком useFieldArray у самого RHF (в нашем случае отвалились destinationPoints),
 * есть даже пара ишью на этот счет:
 *   - [раз](https://github.com/react-hook-form/react-hook-form/issues/832)
 *   - [два](https://github.com/react-hook-form/react-hook-form/issues/1870)
 *
 * поэтому пришлось вкрутить такие костыли для каких-то начальных значений по-умолчанию, которые требуют похода в бек.
 */
export const AsyncDefaultValues = memo<Props>(function AsyncDefaultValues({defaultValues}: Props): null {
  const searchParams = new URLSearchParams(useLocation().search)
  const {reset, watch} = useFormContext<FormValues>()
  const {tariff, zoneName, zonePoint} = appConfig.initialProps
  const addNotification = useAddNotification()

  useTransition(
    async function toAsyncDefaultValues() {
      const from = searchParams.get('from') || undefined
      const to = searchParams.get('to') || undefined
      const preOrderId = searchParams.get('pre_order_id') || undefined

      if (preOrderId) {
        const withPreOrderValues = await fetchPreOrderForm(preOrderId)

        // Костыльно дожидаемся пока в стейте формы не появятся вертикали, чтобы потом корректно их заполнить
        await new Promise<void>(res => {
          function awaitSettings() {
            const formValues = watch()

            if (!formValues?.__settings?.__verticals) {
              setTimeout(awaitSettings, 100)

              return
            }

            res()
          }

          awaitSettings()
        })

        pipe(
          defaultValues,
          tap(logGroupStart('asyncValues', true)),
          merge(watch()),
          tap(log('before merge')),
          withPreOrderValues,
          tap(log('Итоговый стейт')),
          tap(logGroupEnd()),
          reset,
        )

        return
      }

      const syncValues = pipe(
        defaultValues,
        tap(logGroupStart('Начальный стейт формы', true)),
        tap(log('Дефолтный стейт')),
        restoreFormValues,
        tariff ? withTariff(tariff) : identity,
        zoneName ? withZonePoint(zonePoint) : identity,
        tap(log('Итоговый стейт')),
        tap(logGroupEnd()),
      )
      reset(syncValues)

      const isSourcePointEmpty = !getSourceCity(syncValues) && !from && !zoneName

      pipe(
        syncValues,
        tap(logGroupStart('Стейт формы перед асинхронщиной', true)),
        pick(['sourcePoint', 'destinationPoints']),
        tap(log('pick')),
        tap(log('zoneName')),
        from ? await fetchRouteFromQuery(from, to) : identity,
        tap(log('query')),
        zoneName && !from ? await fetchAddressFromInitialProps(zonePoint) : identity,
        tap(log('zoneName')),
        isSourcePointEmpty ? await fetchInitialLocation(appConfig.location, addNotification) : identity,
        tap(log('initialLocation: конец асинхронщины')),
        merge(watch()),
        tap(log('merge with current form')),
        withEmptyDestinationPoint(zoneName),
        tap(log('Итоговый стейт')),
        tap(logGroupEnd()),
        reset,
      )
    },

    {key: 'fetch/default-values'},
  )

  return null
})
